import type { MermaidElementModel } from '@blocksuite/affine-model';
import type { GfxController } from '@blocksuite/std/gfx';
import { type Signal, signal } from '@preact/signals-core';

export type MermaidViewMode = 'diagram' | 'code' | 'split';

const viewModes = new WeakMap<MermaidElementModel, Signal<MermaidViewMode>>();
const virtualSelections = new WeakMap<
  MermaidElementModel,
  Signal<string | null>
>();

export type MermaidVisualEditingState = {
  addPickerEntityId: string | null;
  connectSourceEntityId: string | null;
  inlineEntityId: string | null;
};

const visualEditingStates = new WeakMap<
  MermaidElementModel,
  Signal<MermaidVisualEditingState>
>();

export function getMermaidVisualEditingSignal(model: MermaidElementModel) {
  let state = visualEditingStates.get(model);
  if (!state) {
    state = signal<MermaidVisualEditingState>({
      addPickerEntityId: null,
      connectSourceEntityId: null,
      inlineEntityId: null,
    });
    visualEditingStates.set(model, state);
  }
  return state;
}

const INLINE_EDITABLE_ENTITY_PREFIXES = [
  'node:',
  'state:',
  'class:',
  'entity:',
  'participant:',
  'slice:',
  'task:',
  'item:',
] as const;

export function isMermaidInlineEditableEntityId(entityId: string | null) {
  return Boolean(
    entityId &&
    INLINE_EDITABLE_ENTITY_PREFIXES.some(prefix => entityId.startsWith(prefix))
  );
}

export function setMermaidInlineEditing(
  model: MermaidElementModel,
  entityId: string | null
) {
  getMermaidVisualEditingSignal(model).value = {
    ...getMermaidVisualEditingSignal(model).value,
    inlineEntityId: isMermaidInlineEditableEntityId(entityId) ? entityId : null,
  };
}
export function setMermaidAddPicker(
  model: MermaidElementModel,
  entityId: string | null
) {
  getMermaidVisualEditingSignal(model).value = {
    ...getMermaidVisualEditingSignal(model).value,
    addPickerEntityId: entityId?.startsWith('node:') ? entityId : null,
  };
}

const CONNECT_SOURCE_ENTITY_PREFIXES = [
  'node:',
  'state:',
  'class:',
  'entity:',
] as const;

export function isMermaidConnectSourceEntityId(entityId: string | null) {
  return Boolean(
    entityId &&
    CONNECT_SOURCE_ENTITY_PREFIXES.some(prefix => entityId.startsWith(prefix))
  );
}

export function setMermaidConnectSource(
  model: MermaidElementModel,
  entityId: string | null
) {
  getMermaidVisualEditingSignal(model).value = {
    ...getMermaidVisualEditingSignal(model).value,
    connectSourceEntityId: isMermaidConnectSourceEntityId(entityId)
      ? entityId
      : null,
  };
}

export function getMermaidVirtualSelectionSignal(model: MermaidElementModel) {
  let selection = virtualSelections.get(model);
  if (!selection) {
    selection = signal<string | null>(null);
    virtualSelections.set(model, selection);
  }
  return selection;
}

export function setMermaidVirtualSelection(
  model: MermaidElementModel,
  entityId: string | null
) {
  getMermaidVirtualSelectionSignal(model).value = entityId;
}

export function clearMermaidVirtualSelection(model: MermaidElementModel) {
  virtualSelections.delete(model);
  visualEditingStates.delete(model);
}

export function getMermaidViewModeSignal(model: MermaidElementModel) {
  let mode = viewModes.get(model);
  if (!mode) {
    mode = signal<MermaidViewMode>('diagram');
    viewModes.set(model, mode);
  }
  return mode;
}

export function setMermaidViewMode(
  model: MermaidElementModel,
  mode: MermaidViewMode
) {
  getMermaidViewModeSignal(model).value = mode;
}

export function enterMermaidEditing(
  gfx: GfxController,
  model: MermaidElementModel,
  mode: Exclude<MermaidViewMode, 'diagram'> = 'code'
) {
  setMermaidViewMode(model, mode);
  gfx.selection.set({
    elements: [model.id],
    editing: true,
  });
}

export function enterMermaidVisualEditing(
  gfx: GfxController,
  model: MermaidElementModel,
  entityId: string | null = null,
  options: { inline?: boolean } = {}
) {
  setMermaidViewMode(model, 'diagram');
  setMermaidVirtualSelection(model, entityId);
  setMermaidInlineEditing(model, options.inline === false ? null : entityId);
  gfx.selection.set({
    elements: [model.id],
    editing: true,
  });
}

export function exitMermaidVisualEditing(
  gfx: GfxController,
  model: MermaidElementModel,
  options: { keepOuterSelection?: boolean } = {}
) {
  setMermaidViewMode(model, 'diagram');
  setMermaidVirtualSelection(model, null);
  setMermaidInlineEditing(model, null);
  setMermaidAddPicker(model, null);
  setMermaidConnectSource(model, null);
  if (options.keepOuterSelection ?? true) {
    gfx.selection.set({
      elements: [model.id],
      editing: false,
    });
  } else {
    gfx.selection.clear();
  }
}

export function showMermaidDiagram(
  gfx: GfxController,
  model: MermaidElementModel
) {
  exitMermaidVisualEditing(gfx, model);
}
